import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';

import type {
  DocumentRecipient,
  DocumentStatus,
  DocumentSummary,
  DocumentTimelineEvent,
} from '../../models/document.models';
import { DocumentApiService } from '../../services/document-api.service';
import { GlobalFeedbackService } from '../../services/global-feedback.service';
import { UserDateTimeFormatService } from '../../services/user-date-time-format.service';
import { normalizeApiError } from '../../utils/error-normalizer';

type DocumentActionId =
  | 'download_document'
  | 'download_audit_report'
  | 'download_attachment'
  | 'delete'
  | 'edit'
  | 'revoke'
  | 'send_reminder';

type OverviewTabId = 'recipients' | 'history';

interface DocumentAction {
  id: DocumentActionId;
  label: string;
  available: boolean;
  destructive?: boolean;
}

interface SummaryItem {
  label: string;
  value: string;
  multiline?: boolean;
}

interface DocumentsListQueryState {
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

@Component({
  selector: 'app-document-view',
  imports: [CommonModule],
  templateUrl: './document-view.component.html',
  styleUrl: './document-view.component.scss',
})
export class DocumentViewComponent implements OnInit {
  document: DocumentSummary | null = null;
  loading = false;
  errorMessage = '';
  successMessage = '';
  activeTab: OverviewTabId = 'recipients';
  menuOpen = false;
  deleteConfirmOpen = false;
  busyActionId: DocumentActionId | null = null;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(DocumentApiService);
  private readonly feedback = inject(GlobalFeedbackService);
  private readonly dateTimeFormat = inject(UserDateTimeFormatService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly documentRef = inject(DOCUMENT);

  private readonly statusActions: Partial<Record<DocumentStatus, DocumentAction[]>> = {
    completed: [
      { id: 'download_document', label: 'Download document', available: true },
      { id: 'download_audit_report', label: 'Download audit report', available: true },
      { id: 'delete', label: 'Delete', available: true, destructive: true },
    ],
    waiting_for_others: [
      { id: 'download_document', label: 'Download document', available: true },
      { id: 'edit', label: 'Edit document', available: false },
      { id: 'revoke', label: 'Revoke', available: true, destructive: true },
      { id: 'send_reminder', label: 'Send reminder', available: true },
    ],
    draft: [
      { id: 'edit', label: 'Edit document', available: true },
      { id: 'download_document', label: 'Download document', available: true },
    ],
    scheduled: [
      { id: 'edit', label: 'Edit document', available: true },
      { id: 'download_document', label: 'Download document', available: true },
    ],
    needs_attention: [
      { id: 'edit', label: 'Edit document', available: true },
      { id: 'download_document', label: 'Download document', available: true },
    ],
    declined: [{ id: 'download_document', label: 'Download document', available: true }],
    revoked: [{ id: 'download_document', label: 'Download document', available: true }],
    expired: [
      { id: 'download_document', label: 'Download document', available: true },
      { id: 'delete', label: 'Delete', available: true, destructive: true },
    ],
  };

  ngOnInit(): void {
    const documentId = this.route.snapshot.paramMap.get('id');
    if (!documentId) {
      this.errorMessage = 'Document id is missing.';
      return;
    }

    this.loadDocument(documentId);
  }

  get breadcrumbTitle(): string {
    const title = this.document?.document.title?.trim() || 'Document overview';
    return title.length > 48 ? `${title.slice(0, 45)}...` : title;
  }

  get listQueryParams(): DocumentsListQueryState {
    const params = this.route.snapshot.queryParamMap;
    const state: DocumentsListQueryState = {};
    const status = params.get('status');
    const search = params.get('search');
    const page = this.parsePositiveInteger(params.get('page'));
    const limit = this.parsePositiveInteger(params.get('limit'));

    if (status) {
      state.status = status;
    }

    if (search) {
      state.search = search;
    }

    if (page) {
      state.page = page;
    }

    if (limit) {
      state.limit = limit;
    }

    return state;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.menuOpen) {
      return;
    }

    const target = event.target as Node | null;
    if (target && this.hostElement.nativeElement.contains(target)) {
      return;
    }

    this.closeMenu();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.deleteConfirmOpen) {
      this.closeDeleteConfirm();
      return;
    }

    this.closeMenu();
  }

  get primaryAction(): DocumentAction | null {
    const document = this.document;
    if (!document) {
      return null;
    }

    const actions = this.actionsFor(document);
    if (document.status === 'waiting_for_others') {
      return actions.find((action) => action.id === 'send_reminder') ?? null;
    }

    if (document.status === 'draft' || document.status === 'scheduled' || document.status === 'needs_attention') {
      return actions.find((action) => action.id === 'edit') ?? null;
    }

    return actions.find((action) => action.id === 'download_document') ?? null;
  }

  get menuActions(): DocumentAction[] {
    const document = this.document;
    if (!document) {
      return [];
    }

    const primaryActionId = this.primaryAction?.id;
    return this.actionsFor(document).filter((action) => action.id !== primaryActionId);
  }

  get summaryItems(): SummaryItem[] {
    const document = this.document;
    if (!document) {
      return [];
    }

    const items: SummaryItem[] = [
      { label: 'Document ID', value: document._id },
      { label: 'Tags', value: document.tags.length ? document.tags.join(', ') : 'No tags' },
      {
        label: 'Status',
        value: this.statusNote(document)
          ? `${this.statusLabel(document.status)} (${this.statusNote(document)})`
          : this.statusLabel(document.status),
      },
      { label: 'Sent by', value: 'You' },
    ];

    if (document.status === 'draft') {
      items.push({ label: 'Created on', value: this.formatDateTime(document.createdAt) });
      items.push({ label: 'Last updated', value: this.formatDateTime(document.updatedAt) });
    } else {
      if (document.sentAt) {
        items.push({ label: 'Sent on', value: this.formatDateTime(document.sentAt) });
      }

      const lastActivity = this.lastActivityLabel(document);
      if (lastActivity) {
        items.push({ label: 'Last activity', value: lastActivity });
      }
    }

    if (document.status === 'completed' && document.completedAt) {
      items.push({ label: 'Completed on', value: this.formatDateTime(document.completedAt) });
    }

    if (document.status === 'scheduled' && document.scheduledFor) {
      items.push({ label: 'Scheduled for', value: this.formatDateTime(document.scheduledFor) });
    }

    if (document.status === 'waiting_for_others') {
      items.push({ label: 'Expires on', value: this.formatDateTime(document.expiryAt) });
    }

    if (document.status === 'expired') {
      items.push({ label: 'Expired on', value: this.formatDateTime(document.expiryAt) });
    }

    if (document.status === 'revoked' && document.revokedAt) {
      items.push({ label: 'Revoked on', value: this.formatDateTime(document.revokedAt) });
    }

    items.push({ label: 'File(s)', value: document.sourceFile.originalName });
    items.push({ label: 'Recipients', value: this.recipientSummary(document) });

    if (document.document.message?.trim()) {
      items.push({
        label: 'Message',
        value: document.document.message.trim(),
        multiline: true,
      });
    }

    if (document.ccEmails.length) {
      items.push({ label: 'CC', value: document.ccEmails.join(', ') });
    }

    items.push({
      label: 'Brand Profile',
      value:
        document.documentSettings.brandName?.trim() ||
        document.documentSettings.brandId?.trim() ||
        document.documentSettings.brand?.trim() ||
        'Default brand',
    });

    return items;
  }

  get recipientRows(): Array<{
    id: string;
    name: string;
    email: string;
    deliveryMode: string;
    lastActivity: string;
    statusLabel: string;
    statusClass: string;
    authentication: string;
  }> {
    const document = this.document;
    if (!document) {
      return [];
    }

    return document.recipients.map((recipient) => ({
      id: recipient.id,
      name: recipient.name,
      email: recipient.email,
      deliveryMode: 'Email',
      lastActivity: this.recipientLastActivityLabel(recipient),
      statusLabel: this.recipientStatusLabel(recipient),
      statusClass: `recipient-status-${recipient.status}`,
      authentication: '-',
    }));
  }

  get historyRows(): Array<{
    at: string;
    message: string;
    action: string;
  }> {
    return [...(this.document?.timeline ?? [])]
      .sort((left, right) => new Date(right.at).getTime() - new Date(left.at).getTime())
      .map((event) => ({
        at: this.formatDateTime(event.at),
        message: event.message,
        action: this.timelineActionLabel(event),
      }));
  }

  statusLabel(status: DocumentStatus): string {
    return status.replaceAll('_', ' ');
  }

  retry(): void {
    const documentId = this.route.snapshot.paramMap.get('id');
    if (!documentId) {
      this.errorMessage = 'Document id is missing.';
      return;
    }

    this.loadDocument(documentId);
  }

  selectTab(tab: OverviewTabId): void {
    this.activeTab = tab;
  }

  navigateBackToList(): void {
    void this.router.navigate(['/documents'], {
      queryParams: this.listQueryParams,
    });
  }

  toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.menuOpen = !this.menuOpen;
  }

  handleAction(event: Event, action: DocumentAction): void {
    event.stopPropagation();
    if (!this.document || !action.available || this.loading || this.isBusy(action.id)) {
      return;
    }

    switch (action.id) {
      case 'download_document':
        this.downloadDocument();
        return;
      case 'download_audit_report':
        this.downloadAuditReport();
        return;
      case 'edit':
        void this.router.navigate(['/documents', this.document._id, 'edit']);
        return;
      case 'revoke':
        this.revokeDocument();
        return;
      case 'send_reminder':
        this.sendReminder();
        return;
      case 'delete':
        this.deleteConfirmOpen = true;
        this.closeMenu();
        return;
      default:
        return;
    }
  }

  closeDeleteConfirm(): void {
    this.deleteConfirmOpen = false;
    this.cdr.markForCheck();
  }

  confirmDelete(event: Event): void {
    event.stopPropagation();
    if (!this.document || this.isBusy('delete')) {
      return;
    }

    this.successMessage = '';
    this.setBusyAction('delete');
    this.api
      .deleteDocument(this.document._id)
      .pipe(
        finalize(() => {
          this.clearBusyAction();
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.feedback.clearError();
          this.deleteConfirmOpen = false;
          this.successMessage = 'Document deleted successfully.';
          this.navigateBackToList();
        },
        error: (error: unknown) => {
          this.feedback.showNormalizedError(error, {
            fallbackMessage: 'Failed to delete document',
          });
          this.cdr.markForCheck();
        },
      });
  }

  isBusy(actionId: DocumentActionId): boolean {
    return this.busyActionId === actionId;
  }

  private actionsFor(document: DocumentSummary): DocumentAction[] {
    return (this.statusActions[document.status] ?? []).filter((action) => {
      if (action.id === 'download_audit_report') {
        return Boolean(document.signatureCertificateFile);
      }

      return action.available;
    });
  }

  private loadDocument(documentId: string): void {
    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.document = null;
    this.menuOpen = false;
    this.deleteConfirmOpen = false;

    this.api
      .getDocument(documentId)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (document) => {
          this.feedback.clearError();
          this.document = document;
          this.activeTab = this.defaultTabFor(document.status);
          this.errorMessage = '';
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, {
            fallbackMessage: 'Failed to load document overview',
          });
          this.errorMessage = normalized.message;
          this.feedback.showError({
            ...normalized,
            actionLabel: 'Retry',
            action: () => this.retry(),
          });
          this.cdr.markForCheck();
        },
      });
  }

  private defaultTabFor(status: DocumentStatus): OverviewTabId {
    return status === 'expired' ? 'history' : 'recipients';
  }

  private closeMenu(): void {
    if (!this.menuOpen) {
      return;
    }

    this.menuOpen = false;
    this.cdr.markForCheck();
  }

  private setBusyAction(actionId: DocumentActionId): void {
    this.busyActionId = actionId;
  }

  private clearBusyAction(): void {
    this.busyActionId = null;
  }

  private downloadDocument(): void {
    if (!this.document) {
      return;
    }

    this.successMessage = '';
    this.setBusyAction('download_document');
    const download$ =
      this.document.status === 'completed'
        ? this.api.downloadFinalPdf(this.document._id)
        : this.api.downloadSourcePdf(this.document.sourceFile.fileId);

    download$
      .pipe(
        finalize(() => {
          this.clearBusyAction();
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => {
          this.feedback.clearError();
          this.closeMenu();
          this.downloadBlob(blob, this.resolveDownloadName(this.document!));
        },
        error: (error: unknown) => {
          this.feedback.showNormalizedError(error, {
            fallbackMessage: 'Failed to download document',
          });
          this.cdr.markForCheck();
        },
      });
  }

  private downloadAuditReport(): void {
    if (!this.document?.signatureCertificateFile) {
      return;
    }

    this.successMessage = '';
    this.setBusyAction('download_audit_report');
    this.api
      .downloadSignatureCertificate(this.document._id)
      .pipe(
        finalize(() => {
          this.clearBusyAction();
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => {
          this.feedback.clearError();
          this.closeMenu();
          this.downloadBlob(blob, this.resolveAuditReportDownloadName(this.document!));
        },
        error: (error: unknown) => {
          this.feedback.showNormalizedError(error, {
            fallbackMessage: 'Failed to download audit report',
          });
          this.cdr.markForCheck();
        },
      });
  }

  private revokeDocument(): void {
    if (!this.document || this.document.status !== 'waiting_for_others') {
      return;
    }

    this.successMessage = '';
    this.setBusyAction('revoke');
    this.api
      .revokeDocument(this.document._id)
      .pipe(
        finalize(() => {
          this.clearBusyAction();
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (document) => {
          this.feedback.clearError();
          this.closeMenu();
          this.successMessage = 'Document revoked successfully.';
          this.document = document;
          this.activeTab = this.defaultTabFor(document.status);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.feedback.showNormalizedError(error, {
            fallbackMessage: 'Failed to revoke document',
          });
          this.cdr.markForCheck();
        },
      });
  }

  private sendReminder(): void {
    if (!this.document || this.document.status !== 'waiting_for_others') {
      return;
    }

    this.successMessage = '';
    this.setBusyAction('send_reminder');
    this.api
      .sendReminder(this.document._id)
      .pipe(
        finalize(() => {
          this.clearBusyAction();
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.feedback.clearError();
          this.closeMenu();
          const recipientCount = response.queuedRecipientIds.length;
          this.successMessage =
            recipientCount === 1
              ? 'Reminder queued for 1 recipient.'
              : `Reminders queued for ${recipientCount} recipients.`;

          if (response.document) {
            this.document = response.document;
            this.activeTab = this.defaultTabFor(response.document.status);
          } else {
            this.loadDocument(this.document!._id);
          }

          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.feedback.showNormalizedError(error, {
            fallbackMessage: 'Failed to send reminder',
          });
          this.cdr.markForCheck();
        },
      });
  }

  private resolveDownloadName(document: DocumentSummary): string {
    if (document.status !== 'completed') {
      return document.sourceFile.originalName;
    }

    const baseName =
      document.finalSignedFile?.originalName ||
      document.sourceFile.originalName.replace(/\.pdf$/i, '') + '-signed.pdf';
    return baseName.toLowerCase().endsWith('.pdf') ? baseName : `${baseName}.pdf`;
  }

  private resolveAuditReportDownloadName(document: DocumentSummary): string {
    const baseName =
      document.signatureCertificateFile?.originalName ||
      `${this.slugifyFileName(document.document.title || 'document')}-signature-certificate.pdf`;
    return baseName.toLowerCase().endsWith('.pdf') ? baseName : `${baseName}.pdf`;
  }

  private slugifyFileName(value: string): string {
    const normalized = value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return normalized || 'document';
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = this.documentRef.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.style.display = 'none';
    this.documentRef.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  private recipientSummary(document: DocumentSummary): string {
    const count = document.recipients.length;
    return count === 1 ? '1 recipient' : `${count} recipients`;
  }

  private lastActivityLabel(document: DocumentSummary): string | null {
    const latestEvent = this.latestTimelineEvent(document);
    if (!latestEvent) {
      return null;
    }

    return `${this.formatDateTime(latestEvent.at)} (${latestEvent.message})`;
  }

  private latestTimelineEvent(document: DocumentSummary): DocumentTimelineEvent | null {
    const timeline = document.timeline ?? [];
    if (!timeline.length) {
      return null;
    }

    return [...timeline].sort((left, right) => new Date(right.at).getTime() - new Date(left.at).getTime())[0] ?? null;
  }

  private statusNote(document: DocumentSummary): string | null {
    switch (document.status) {
      case 'completed': {
        const completedRecipient = document.recipients.find((recipient) => recipient.status === 'completed');
        return completedRecipient ? `Signed by ${completedRecipient.name}` : 'Signed';
      }
      case 'waiting_for_others': {
        const nextRecipient = document.recipients.find(
          (recipient) => recipient.status === 'invited' || recipient.status === 'viewed',
        );
        return nextRecipient ? `Needs action from ${nextRecipient.name}` : 'Waiting on recipients';
      }
      case 'expired':
        return `Expired on ${this.formatDateTime(document.expiryAt)}`;
      case 'declined': {
        const declinedRecipient = document.recipients.find((recipient) => recipient.status === 'declined');
        return declinedRecipient ? `Declined by ${declinedRecipient.name}` : 'Declined';
      }
      case 'revoked':
        return document.revokedAt ? `Revoked on ${this.formatDateTime(document.revokedAt)}` : 'Document revoked';
      case 'scheduled':
        return document.scheduledFor ? `Scheduled for ${this.formatDateTime(document.scheduledFor)}` : 'Scheduled';
      case 'needs_attention':
        return document.statusReason ?? 'Needs attention';
      case 'draft':
        return 'Draft saved';
      default:
        return document.statusReason ?? null;
    }
  }

  private recipientLastActivityLabel(recipient: DocumentRecipient): string {
    if (recipient.completedAt) {
      return this.formatDateTime(recipient.completedAt);
    }

    if (recipient.declinedAt) {
      return this.formatDateTime(recipient.declinedAt);
    }

    if (recipient.viewedAt) {
      return this.formatDateTime(recipient.viewedAt);
    }

    if (recipient.invitedAt) {
      return this.formatDateTime(recipient.invitedAt);
    }

    return '-';
  }

  private recipientStatusLabel(recipient: DocumentRecipient): string {
    switch (recipient.status) {
      case 'pending':
        return 'Pending';
      case 'invited':
        return 'Invited';
      case 'viewed':
        return 'Viewed';
      case 'completed':
        return 'Signed';
      case 'declined':
        return 'Declined';
      case 'expired':
        return 'Expired';
      case 'delivery_failed':
        return 'Delivery failed';
      case 'revoked':
        return 'Revoked';
      default:
        return recipient.status;
    }
  }

  private timelineActionLabel(event: DocumentTimelineEvent): string {
    const formatted = event.event
      .split('.')
      .at(-1)
      ?.replaceAll('_', ' ')
      .trim();

    if (!formatted) {
      return 'Updated';
    }

    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  private formatDateTime(value?: string): string {
    return value ? this.dateTimeFormat.formatDateTime(value) : '-';
  }

  private parsePositiveInteger(value: string | null): number | null {
    if (!value) {
      return null;
    }

    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
}
